import React, { useState, useEffect, useRef, forwardRef, Suspense } from 'react';
import { TextField, IconButton, Popover, Box } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import InsertEmoticonIcon from '@mui/icons-material/InsertEmoticon';
import type { EmojiClickData, Theme as EmojiTheme } from 'emoji-picker-react';

const EmojiPicker = React.lazy(() => import('emoji-picker-react'));

/**
 * Props for the EmojiInput component.
 */
export interface EmojiInputProps extends Omit<TextFieldProps, 'onChange' | 'value'> {
  disabled?: boolean;
  /** Whether to place the emoji trigger before or after the text field */
  emojiButtonPosition?: 'start' | 'end';
  inputRef?: React.Ref<HTMLInputElement | HTMLTextAreaElement>;
  multiline?: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Show the emoji picker toggle button */
  showEmojiPicker?: boolean;
  value: string;
  variant?: 'outlined' | 'filled' | 'standard';
  /**
   * Drive the emoji picker theme explicitly.
   * Pass `true` for dark, `false` (default) for light.
   * This removes the dependency on any app-level ThemeContext.
   */
  isDarkMode?: boolean;
}

/**
 * Text input with an optional emoji picker popover.
 *
 * Inserts the selected emoji at the current cursor position and
 * restores focus/selection afterwards. Supports both single-line
 * and multiline modes.
 *
 * The emoji picker theme follows the `isDarkMode` prop so the
 * parent can sync it with whatever theme context the app uses.
 *
 * @example
 * <EmojiInput
 *   value={text}
 *   onChange={setText}
 *   showEmojiPicker
 *   isDarkMode={colorMode === 'dark'}
 * />
 */
const EmojiInput = forwardRef<HTMLInputElement | HTMLTextAreaElement, EmojiInputProps>(
  (
    {
      disabled = false,
      emojiButtonPosition = 'end',
      inputRef,
      multiline = false,
      onChange,
      placeholder = 'Type your message...',
      showEmojiPicker = false,
      value,
      variant = 'outlined',
      isDarkMode = false,
      ...props
    },
    ref,
  ) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [cursorPosition, setCursorPosition] = useState<number | null>(null);
    const internalRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);

    const handleEmojiButtonClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(anchorEl ? null : event.currentTarget);
    };

    const open = Boolean(anchorEl);

    const onEmojiClick = (emojiData: EmojiClickData) => {
      const emoji = emojiData.emoji;
      const input = internalRef.current;
      if (!input) return;

      const start = value.substring(0, input.selectionStart ?? 0);
      const end = value.substring(input.selectionEnd ?? 0);
      const newValue = start + emoji + end;

      onChange(newValue);
      setCursorPosition(start.length + emoji.length);
      setAnchorEl(null);
    };

    // Restore focus + cursor after emoji insertion
    useEffect(() => {
      if (internalRef.current && cursorPosition !== null) {
        internalRef.current.focus();
        internalRef.current.setSelectionRange(cursorPosition, cursorPosition);
      }
    }, [cursorPosition]);

    // Forward the internal ref to the consumer's inputRef
    useEffect(() => {
      if (!inputRef) return;
      if (typeof inputRef === 'function') {
        inputRef(internalRef.current);
      } else {
        (
          inputRef as React.MutableRefObject<HTMLInputElement | HTMLTextAreaElement | null>
        ).current = internalRef.current;
      }
    }, [inputRef]);

    const emojiButton = showEmojiPicker ? (
      <>
        <IconButton onClick={handleEmojiButtonClick} disabled={disabled} size="small">
          <InsertEmoticonIcon fontSize="small" />
        </IconButton>
        <Popover
          open={open}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        >
          <Suspense fallback={null}>
            <EmojiPicker
              onEmojiClick={onEmojiClick}
              autoFocusSearch={false}
              theme={(isDarkMode ? 'dark' : 'light') as EmojiTheme}
              lazyLoadEmojis
            />
          </Suspense>
        </Popover>
      </>
    ) : null;

    return (
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        sx={{ width: '100%', backgroundColor: 'transparent' }}
      >
        {emojiButtonPosition === 'start' && emojiButton}
        <TextField
          fullWidth
          inputRef={internalRef}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          multiline={multiline}
          placeholder={placeholder}
          variant={variant}
          disabled={disabled}
          ref={ref as React.Ref<HTMLDivElement>}
          {...props}
        />
        {emojiButtonPosition === 'end' && emojiButton}
      </Box>
    );
  },
);

EmojiInput.displayName = 'EmojiInput';
export default EmojiInput;
